'use client';

import { c as _c } from "react/compiler-runtime";
import { useWallets } from '@morfi/backend-sdk-v2/client';
import { formatFiatUnits, fundingSettlementValue, OnrampApiError, type OnrampClient, withdrawalFeeEstimate } from '@morfi/onramp-sdk';
import { useFundingAmountDraft, useFundingCurrency, useWithdrawalAmount, useWithdrawalCheckout } from '@morfi/onramp-sdk/react';
import { useLocale, useTranslations } from 'next-intl';
import { type ChangeEvent, type ReactNode, useEffect, useEffectEvent, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { InputField } from '@/components/ui/input-field';
import { getUsdcMint } from '@/config/solana';
import { useTransferUsdc } from '@/hooks/wallet/use-transfer-usdc';
import { getActiveNetwork } from '@/lib/network/active';
import { logger } from '@/lib/telemetry/logger';
import { isWithdrawalRecipient } from '@/lib/wallet/withdraw-recipient';
import { useExchangeRates } from '@/providers/exchange-rate-provider';
import { useCurrencyStore } from '@/stores/currency';
import { FundingFees } from '../checkout/amount/funding-fees';
import { FundingHistory } from '../history/funding-history';
import { fundingAmountStorage } from '../shared/funding-amount-storage';
import { FundingChromeContext, useFundingChrome } from '../shared/funding-chrome';
import { FundingDialog } from '../shared/funding-dialog';
import { FundingError } from '../shared/funding-error';
import { FundingLoading } from '../shared/funding-loading';
import { FundingProviderLoading } from '../shared/funding-provider-loading';
import { useFundingRealtime } from '../shared/funding-realtime';
import { WithdrawAmountScreen } from './withdraw-amount-screen';
import { WithdrawMethodPicker } from './withdraw-method-picker';
import { WithdrawOperationScreen } from './withdraw-operation-screen';
export function WithdrawCheckout(t0) {
  const $ = _c(152);
  const {
    client,
    open,
    onClose,
    onProfile
  } = t0;
  const t = useTranslations("onramp");
  const locale = useLocale();
  const selectedCurrency = useCurrencyStore(_temp);
  const realtime = useFundingRealtime(client, open);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = crypto.randomUUID.bind(crypto);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const flow = useWithdrawalCheckout(client, "web", t1, open, selectedCurrency, locale, realtime);
  let t2;
  if ($[1] !== flow.screen) {
    t2 = () => {
      logger.warn("offramp.command_failed", {
        stage: flow.screen
      });
    };
    $[1] = flow.screen;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const reportCommandFailure = useEffectEvent(t2);
  let t3;
  if ($[3] !== flow.commands.error || $[4] !== reportCommandFailure) {
    t3 = () => {
      if (flow.commands.error) {
        reportCommandFailure();
      }
    };
    $[3] = flow.commands.error;
    $[4] = reportCommandFailure;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== flow.commands.error) {
    t4 = [flow.commands.error];
    $[6] = flow.commands.error;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  useEffect(t3, t4);
  const {
    wallets
  } = useWallets();
  const transfer = useTransferUsdc();
  const [historyOpen, setHistoryOpen] = useState(false);
  let t5;
  if ($[8] !== t) {
    t5 = t("withdraw_title");
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const chrome = useFundingChrome(t5);
  const context = flow.context.data;
  const rates = useExchangeRates();
  const currency = useFundingCurrency(flow.currency, rates.fundingFx);
  let t6;
  if ($[10] !== client.progressKey) {
    t6 = client.progressKey ? {
      persistence: {
        scope: `${client.progressKey}:${getActiveNetwork()}`,
        direction: "offramp" as const,
        storage: fundingAmountStorage
      }
    } : {};
    $[10] = client.progressKey;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== currency.currency || $[13] !== currency.decimals || $[14] !== flow.method || $[15] !== locale || $[16] !== t6) {
    t7 = {
      method: flow.method,
      currency: currency.currency,
      decimals: currency.decimals,
      locale,
      amountUnits: null,
      ...t6
    };
    $[12] = currency.currency;
    $[13] = currency.decimals;
    $[14] = flow.method;
    $[15] = locale;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const {
    value,
    setValue,
    units: inputUnits
  } = useFundingAmountDraft(t7);
  const amount = useWithdrawalAmount(client, flow.method, "web", currency, inputUnits, context?.balanceD6 ?? "0", open && flow.screen === "amount", context);
  let reviewUnits;
  let t8;
  if ($[18] !== amount || $[19] !== currency.currency || $[20] !== currency.decimals || $[21] !== currency.fx || $[22] !== currency.status || $[23] !== flow.amount || $[24] !== flow.preview || $[25] !== flow.screen) {
    reviewUnits = currency.status === "ready" && currency.fx ? fundingSettlementValue(flow.amount || "0", currency.currency, currency.decimals, currency.fx) : null;
    t8 = flow.screen === "amount" ? amount.estimate : withdrawalFeeEstimate(flow.preview, flow.amount, reviewUnits, currency.currency, currency.decimals);
    $[18] = amount;
    $[19] = currency.currency;
    $[20] = currency.decimals;
    $[21] = currency.fx;
    $[22] = currency.status;
    $[23] = flow.amount;
    $[24] = flow.preview;
    $[25] = flow.screen;
    $[26] = reviewUnits;
    $[27] = t8;
  } else {
    reviewUnits = $[26];
    t8 = $[27];
  }
  const feeEstimate = t8;
  const operation = flow.operation.data;
  const requestError = (flow.context.isError ? flow.context.error : null) ?? (flow.activeId && flow.operation.isError ? flow.operation.error : null) ?? flow.resumeError ?? (flow.commands.isError ? flow.commands.error : null) ?? (flow.screen === "amount" && amount.quote.isError ? amount.quote.error : null);
  const expired = requestError instanceof OnrampApiError && requestError.status === 401;
  const actionLabel = expired ? "sign_in" : flow.commands.isError ? "retry" : "continue";
  let t9;
  if ($[28] !== expired || $[29] !== flow || $[30] !== onClose) {
    t9 = function retryRequest() {
      if (expired) {
        onClose();
      } else {
        if (flow.activeId) {
          flow.retryOperation();
        } else {
          flow.context.refetch();
        }
      }
    };
    $[28] = expired;
    $[29] = flow;
    $[30] = onClose;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  const retryRequest = t9;
  let t10;
  if ($[32] !== flow.recipient || $[33] !== wallets) {
    t10 = isWithdrawalRecipient(flow.recipient, wallets.map(_temp2));
    $[32] = flow.recipient;
    $[33] = wallets;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  const validRecipient = t10;
  let t11;
  if ($[35] !== expired || $[36] !== flow.commands || $[37] !== onClose) {
    t11 = function run(command) {
      if (expired) {
        onClose();
        return;
      }
      flow.commands.mutateAsync(command).catch(_temp3);
    };
    $[35] = expired;
    $[36] = flow.commands;
    $[37] = onClose;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  const run = t11;
  let t12;
  if ($[39] !== transfer || $[40] !== wallets) {
    t12 = async function send(source, recipient, amountD6, network, operationId, mint, beforeBroadcast) {
      if ((getActiveNetwork() === "mainnet" ? "mainnet" : "devnet") !== network || getUsdcMint(network) !== mint) {
        throw new Error("offramp_network_changed");
      }
      const wallet_0 = wallets.find(entry => entry.address === source && entry.archivedAt === null);
      if (!wallet_0) {
        throw new Error("offramp_wallet_unavailable");
      }
      const signature = await transfer.submit(wallet_0.id, source, wallet_0.type, recipient, BigInt(amountD6), operationId, network, beforeBroadcast);
      if (!signature) {
        throw new Error("offramp_submission_unknown");
      }
      return signature;
    };
    $[39] = transfer;
    $[40] = wallets;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  const send = t12;
  let t13;
  if ($[42] !== amount || $[43] !== context?.decimals || $[44] !== currency.currency || $[45] !== currency.decimals || $[46] !== feeEstimate || $[47] !== flow.currency || $[48] !== flow.method || $[49] !== flow.screen || $[50] !== inputUnits || $[51] !== locale || $[52] !== reviewUnits || $[53] !== t) {
    t13 = <>{flow.method === "manual_withdraw" ? <div className="flex items-center justify-between text-caption text-foreground-secondary"><span>{t("fees")}</span><span>{formatFiatUnits("0", currency.decimals, currency.currency, locale)}</span></div> : <FundingFees currency={flow.currency} decimals={context?.decimals ?? 2} principalUnits={flow.screen === "amount" ? inputUnits : reviewUnits} estimate={feeEstimate} direction="withdraw" walletDebitUnits={flow.screen === "amount" ? inputUnits : reviewUnits} loading={flow.screen === "amount" && amount.loading} />}</>;
    $[42] = amount;
    $[43] = context?.decimals;
    $[44] = currency.currency;
    $[45] = currency.decimals;
    $[46] = feeEstimate;
    $[47] = flow.currency;
    $[48] = flow.method;
    $[49] = flow.screen;
    $[50] = inputUnits;
    $[51] = locale;
    $[52] = reviewUnits;
    $[53] = t;
    $[54] = t13;
  } else {
    t13 = $[54];
  }
  const fees = t13;
  const t14 = flow.method === "manual_withdraw" && flow.screen !== "payouts" ? "morfi" : "moonpay";
  const t15 = open && !historyOpen;
  let t16;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => setHistoryOpen(true);
    $[55] = t16;
  } else {
    t16 = $[55];
  }
  const t17 = flow.screen === "payouts";
  const t18 = flow.screen !== "payouts";
  let t19;
  if ($[56] !== chrome || $[57] !== flow.back || $[58] !== onProfile || $[59] !== t17 || $[60] !== t18) {
    t19 = {
      ...chrome,
      hasProfile: true,
      openProfile: onProfile,
      picker: t17,
      hasBack: t18,
      back: flow.back
    };
    $[56] = chrome;
    $[57] = flow.back;
    $[58] = onProfile;
    $[59] = t17;
    $[60] = t18;
    $[61] = t19;
  } else {
    t19 = $[61];
  }
  let t20;
  if ($[62] !== flow.activeId || $[63] !== flow.context.isPending || $[64] !== flow.hostedUrl || $[65] !== flow.operation.isPending || $[66] !== flow.screen) {
    t20 = flow.activeId && !flow.hostedUrl && flow.operation.isPending ? <FundingProviderLoading /> : !flow.activeId && flow.context.isPending ? <FundingLoading withdrawal={flow.screen === "payouts" && !flow.activeId} /> : null;
    $[62] = flow.activeId;
    $[63] = flow.context.isPending;
    $[64] = flow.hostedUrl;
    $[65] = flow.operation.isPending;
    $[66] = flow.screen;
    $[67] = t20;
  } else {
    t20 = $[67];
  }
  let t21;
  if ($[68] !== onClose || $[69] !== requestError || $[70] !== retryRequest) {
    t21 = requestError ? <FundingError withdrawal={true} error={requestError} onSignIn={onClose} onRetry={retryRequest} /> : null;
    $[68] = onClose;
    $[69] = requestError;
    $[70] = retryRequest;
    $[71] = t21;
  } else {
    t21 = $[71];
  }
  let t22;
  if ($[72] !== context || $[73] !== expired || $[74] !== flow.activeId || $[75] !== flow.context.isError || $[76] !== flow.context.isFetching || $[77] !== flow.operation.isError || $[78] !== flow.operation.isFetching || $[79] !== flow.screen || $[80] !== operation || $[81] !== retryRequest || $[82] !== t) {
    t22 = !(flow.screen === "amount" && context) && (flow.context.isError && !context || flow.activeId && flow.operation.isError && !operation) ? <AnimatedButton loading={flow.context.isFetching || flow.operation.isFetching} onClick={retryRequest}>{t(expired ? "sign_in" : "retry")}</AnimatedButton> : null;
    $[72] = context;
    $[73] = expired;
    $[74] = flow.activeId;
    $[75] = flow.context.isError;
    $[76] = flow.context.isFetching;
    $[77] = flow.operation.isError;
    $[78] = flow.operation.isFetching;
    $[79] = flow.screen;
    $[80] = operation;
    $[81] = retryRequest;
    $[82] = t;
    $[83] = t22;
  } else {
    t22 = $[83];
  }
  let t23;
  if ($[84] !== context || $[85] !== flow.activeId || $[86] !== flow.commands.isPending || $[87] !== flow.screen || $[88] !== flow.select || $[89] !== run || $[90] !== t) {
    t23 = flow.screen === "payouts" && context ? <>{flow.activeId ? <AnimatedButton onClick={() => run({
        kind: "create"
      })} loading={flow.commands.isPending}>{t("continue")}</AnimatedButton> : null}<WithdrawMethodPicker methods={context.methods} pending={Boolean(flow.activeId)} onSelect={flow.select} /></> : null;
    $[84] = context;
    $[85] = flow.activeId;
    $[86] = flow.commands.isPending;
    $[87] = flow.screen;
    $[88] = flow.select;
    $[89] = run;
    $[90] = t;
    $[91] = t23;
  } else {
    t23 = $[91];
  }
  let t24;
  if ($[92] !== flow || $[93] !== t || $[94] !== validRecipient) {
    t24 = flow.screen === "recipient" ? <><InputField label={t("recipient_wallet")} value={flow.recipient} onChange={event => flow.setRecipient(event.target.value.trim())} autoComplete="off" /><AnimatedButton disabled={!validRecipient} onClick={flow.recipientReady}>{t("continue")}</AnimatedButton></> : null;
    $[92] = flow;
    $[93] = t;
    $[94] = validRecipient;
    $[95] = t24;
  } else {
    t24 = $[95];
  }
  let t25;
  if ($[96] !== amount || $[97] !== context || $[98] !== currency || $[99] !== expired || $[100] !== fees || $[101] !== flow || $[102] !== retryRequest || $[103] !== run || $[104] !== setValue || $[105] !== value) {
    t25 = flow.screen === "amount" && context ? <WithdrawAmountScreen flow={flow} amount={amount} currency={currency} context={context} value={value} setValue={setValue} expired={expired} retryRequest={retryRequest} run={run} fees={fees} /> : null;
    $[96] = amount;
    $[97] = context;
    $[98] = currency;
    $[99] = expired;
    $[100] = fees;
    $[101] = flow;
    $[102] = retryRequest;
    $[103] = run;
    $[104] = setValue;
    $[105] = value;
    $[106] = t25;
  } else {
    t25 = $[106];
  }
  let t26;
  if ($[107] !== actionLabel || $[108] !== context?.source || $[109] !== currency.currency || $[110] !== currency.decimals || $[111] !== fees || $[112] !== flow.commands.isPending || $[113] !== flow.method || $[114] !== flow.recipient || $[115] !== flow.screen || $[116] !== locale || $[117] !== reviewUnits || $[118] !== run || $[119] !== t) {
    t26 = flow.screen === "review" ? <><h3 className="text-heading-md">{t("withdraw_review")}</h3><p className="text-heading-lg">{reviewUnits === null ? t("fees_pending") : formatFiatUnits(reviewUnits, currency.decimals, currency.currency, locale)}</p><p className="break-all text-caption">{flow.method === "manual_withdraw" ? flow.recipient : t("payout_account_subtitle")}</p><p className="break-all text-caption">{t("withdraw_source")}: {context?.source}</p>{fees}<AnimatedButton loading={flow.commands.isPending} disabled={flow.commands.isPending} onClick={() => run({
        kind: "create"
      })}>{t(actionLabel)}</AnimatedButton></> : null;
    $[107] = actionLabel;
    $[108] = context?.source;
    $[109] = currency.currency;
    $[110] = currency.decimals;
    $[111] = fees;
    $[112] = flow.commands.isPending;
    $[113] = flow.method;
    $[114] = flow.recipient;
    $[115] = flow.screen;
    $[116] = locale;
    $[117] = reviewUnits;
    $[118] = run;
    $[119] = t;
    $[120] = t26;
  } else {
    t26 = $[120];
  }
  let t27;
  if ($[121] !== expired || $[122] !== flow || $[123] !== onClose || $[124] !== retryRequest || $[125] !== run || $[126] !== send) {
    t27 = <WithdrawOperationScreen flow={flow} expired={expired} retryRequest={retryRequest} run={run} send={send} onClose={onClose} />;
    $[121] = expired;
    $[122] = flow;
    $[123] = onClose;
    $[124] = retryRequest;
    $[125] = run;
    $[126] = send;
    $[127] = t27;
  } else {
    t27 = $[127];
  }
  let t28;
  if ($[128] !== t20 || $[129] !== t21 || $[130] !== t22 || $[131] !== t23 || $[132] !== t24 || $[133] !== t25 || $[134] !== t26 || $[135] !== t27) {
    t28 = <div className="rr-block flex flex-col gap-4">{t20}{t21}{t22}{t23}{t24}{t25}{t26}{t27}</div>;
    $[128] = t20;
    $[129] = t21;
    $[130] = t22;
    $[131] = t23;
    $[132] = t24;
    $[133] = t25;
    $[134] = t26;
    $[135] = t27;
    $[136] = t28;
  } else {
    t28 = $[136];
  }
  let t29;
  if ($[137] !== onClose || $[138] !== t14 || $[139] !== t15 || $[140] !== t19 || $[141] !== t28) {
    t29 = <FundingDialog provider={t14} open={t15} onClose={onClose} onHistory={t16} chrome={t19}>{t28}</FundingDialog>;
    $[137] = onClose;
    $[138] = t14;
    $[139] = t15;
    $[140] = t19;
    $[141] = t28;
    $[142] = t29;
  } else {
    t29 = $[142];
  }
  const t30 = open && historyOpen;
  let t31;
  if ($[143] !== client || $[144] !== onClose || $[145] !== realtime || $[146] !== t30) {
    t31 = <FundingHistory realtime={realtime} origin="withdraw" client={client} open={t30} onOpenChange={setHistoryOpen} onSignIn={onClose} />;
    $[143] = client;
    $[144] = onClose;
    $[145] = realtime;
    $[146] = t30;
    $[147] = t31;
  } else {
    t31 = $[147];
  }
  let t32;
  if ($[148] !== chrome.report || $[149] !== t29 || $[150] !== t31) {
    t32 = <FundingChromeContext.Provider value={chrome.report}>{t29}{t31}</FundingChromeContext.Provider>;
    $[148] = chrome.report;
    $[149] = t29;
    $[150] = t31;
    $[151] = t32;
  } else {
    t32 = $[151];
  }
  return t32;
}
function _temp3() {}
function _temp2(wallet) {
  return wallet.address;
}
function _temp(state) {
  return state.currency;
}